// Pure class-composition seam for action surfaces. Two consumers:
//
//   1. <Button> — the wrapper component, when the element is <button>.
//   2. Any non-button element that should LOOK like a button — typically a
//      React Router <Link> — calls `buttonClasses()` directly with the same
//      variant/size vocabulary.
//
// Kept in its own (non-React) module so the React-refresh "only export
// components" rule stays clean on Button.tsx, and so the function can be
// unit-tested without React in scope. The interface here IS the test surface.

export type ButtonVariant =
  | "microsoft"
  | "primary"
  | "secondary"
  | "destructive"
  | "toggle"
  | "chip"
  | "ghost"
  | "icon";

// Control-height scale:
//   sm      — chip-sized, 24px tall. Inline-dense action (chat clear, inline
//             toolbar actions). text-smycles-micro, px-3 py-1.
//   compact — 32px tall (--h-control-sm). Card-row CTAs (Save list rows,
//             SharePage landing actions, PowerDetail "Start" CTA). text-smycles-label,
//             px-4 py-2.
//   md      — 40px tall (--h-control-md). The default. Form rhythm, primary
//             page actions. text-smycles-label, px-5 py-2.5.
//   lg      — 48px tall (--h-control-lg). Hero-only.
//   handle  — 48px square circular control. Slider/threshold handles only.
//   floating — md metrics plus elevation. A button that floats over scrolling
//             content (the match workflow's step footer, the result step's
//             save row) and needs to lift off the rows passing beneath it.
export type ButtonSize = "sm" | "compact" | "md" | "lg" | "handle" | "floating" | "square";

export interface ButtonClassesOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

const VARIANTS: Record<ButtonVariant, string> = {
  microsoft: "smycles-microsoft-button",
  primary: "smycles-button",
  secondary: "smycles-dismiss-button",
  destructive: "smycles-danger-button",
  toggle: "smycles-toggle-button",
  chip: "smycles-chip-button",
  ghost: "text-ink-muted hover:text-ink",
  icon: "text-ink-muted hover:bg-ink/[0.06] hover:text-ink",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "px-3 py-1 text-smycles-micro",
  compact: "px-4 py-2 text-smycles-label",
  md: "px-5 py-2.5 text-smycles-label",
  lg: "px-6 py-3 text-smycles-meta",
  square: "h-8 w-8 p-0 text-smycles-label",
  handle: "h-12 w-12 p-0 text-smycles-meta shadow-xl",
  floating: "px-5 py-2.5 text-smycles-label shadow-xl",
};

const ICON_SIZES: Record<ButtonSize, string> = {
  sm: "h-8 w-8 text-smycles-meta",
  compact: "h-9 w-9 text-smycles-meta",
  md: "h-9 w-9 text-smycles-body",
  lg: "h-12 w-12 text-smycles-title",
  square: "h-8 w-8 p-0 text-smycles-label",
  handle: "h-12 w-12 text-smycles-meta shadow-xl",
  floating: "h-9 w-9 text-smycles-body shadow-xl",
};

const BASE =
  "inline-flex items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui disabled:pointer-events-none disabled:opacity-50";

export function buttonClasses({
  variant = "primary",
  size = "md",
  className = "",
}: ButtonClassesOptions = {}): string {
  const sizeClass = variant === "microsoft" ? "px-3 py-2 text-smycles-body gap-3 rounded-none" : variant === "icon" ? ICON_SIZES[size] : SIZES[size];
  return [variant === "microsoft" ? BASE.replace("rounded-full", "") : BASE, VARIANTS[variant], sizeClass, className]
    .filter(Boolean)
    .join(" ");
}
